import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Plus, Trash, WarningCircle, X, ShieldCheck, DeviceMobile, PencilSimple, ArrowBendUpRight } from "@phosphor-icons/react";
import PbxPhrasebook from "@/pages/admin/PbxPhrasebook";
import PbxAiUsage from "@/pages/admin/PbxAiUsage";
import PbxUnrecognized from "@/pages/admin/PbxUnrecognized";
import PbxAiSettings from "@/pages/admin/PbxAiSettings";

const PERM_LABEL = {
  internal: "Internal only",
  domestic: "Domestic (US/CA)",
  international: "International",
  admin: "Admin (all)",
};

export default function PbxExtensions({ orgAdmin = false }) {
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("extensions");

  const { data: orgs } = useQuery({ queryKey: ["pbx-orgs"], queryFn: async () => (await api.get("/pbx/orgs")).data, enabled: !orgAdmin });
  const { data: depts } = useQuery({ queryKey: ["pbx-depts"], queryFn: async () => (await api.get("/pbx/departments")).data });
  const { data: usersData } = useQuery({
    queryKey: orgAdmin ? ["pbx-members"] : ["admin-users"],
    queryFn: async () => (await api.get(orgAdmin ? "/pbx/members" : "/admin/users")).data,
  });

  const effectiveOrg = orgAdmin ? "" : (orgId || orgs?.orgs?.find((o) => o.is_platform_default)?.id || orgs?.orgs?.[0]?.id || "");

  const { data: extData, isLoading } = useQuery({
    queryKey: ["pbx-extensions", effectiveOrg],
    queryFn: async () => (await api.get("/pbx/extensions", { params: effectiveOrg ? { org_id: effectiveOrg } : {} })).data,
    enabled: orgAdmin || !!effectiveOrg || !!orgs,
  });

  const { data: plan } = useQuery({
    queryKey: ["pbx-plan", effectiveOrg],
    queryFn: async () => (await api.get("/pbx/plan", { params: effectiveOrg ? { org_id: effectiveOrg } : {} })).data,
    enabled: orgAdmin || !!effectiveOrg || !!orgs,
  });
  const atExtLimit = plan && !plan.unlimited && !plan.can_override && plan.used_extensions >= plan.extension_limit;

  const provision = useMutation({
    mutationFn: async () => (await api.post("/pbx/provision-customer", { org_id: effectiveOrg || undefined })).data,
    onSuccess: () => {
      toast.success("Customer phone system provisioned — IVR 1→Booking, 2→Reception, 3→Sales + plan granted");
      qc.invalidateQueries({ queryKey: ["pbx-voice-health"] });
      qc.invalidateQueries({ queryKey: ["pbx-plan"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const { data: voiceHealth } = useQuery({
    queryKey: ["pbx-voice-health", effectiveOrg],
    queryFn: async () => (await api.get("/pbx/voice-health", { params: effectiveOrg ? { org_id: effectiveOrg } : {} })).data,
    enabled: orgAdmin || !!effectiveOrg || !!orgs,
    refetchInterval: 30000,
  });

  const extensions = extData?.extensions || [];
  const departments = depts?.departments || [];
  const members = orgAdmin ? (usersData?.members || []) : (usersData?.users || []);

  const { data: teleData } = useQuery({
    queryKey: ["telephony-settings-ivr"],
    queryFn: async () => (await api.get("/telephony/settings")).data,
  });
  const ivrOptions = (teleData?.settings?.ivr_options || [])
    .filter((o) => (o.destination || "").trim())
    .sort((a, b) => String(a.digit || "").localeCompare(String(b.digit || "")));
  const DEST_HINT = (d) => {
    const t = String(d || "").trim().toLowerCase();
    if (["ai_booking", "ai-booking", "appointments", "booking"].includes(t)) return "SI Booking";
    if (["ai_receptionist", "ai-receptionist", "receptionist", "front_desk", "reception"].includes(t)) return "SI Receptionist";
    if (["sales", "voice-sales", "voice_sales", "ai-sales", "ai_sales", "seller", "ai_seller"].includes(t)) return "SI Sales";
    if (["all", "extensions", "all_extensions"].includes(t)) return "Ring all";
    if (/^\+?\d{6,}$/.test(t)) return "Forward";
    return "";
  };

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/pbx/extensions/${id}`)).data,
    onSuccess: () => { toast.success("Extension removed"); qc.invalidateQueries({ queryKey: ["pbx-extensions"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }) => (await api.put(`/pbx/extensions/${id}`, { active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pbx-extensions"] }),
    onError: (e) => toast.error(apiError(e)),
  });

  const grouped = useMemo(() => {
    const g = {};
    for (const e of extensions) (g[e.department] = g[e.department] || []).push(e);
    return g;
  }, [extensions]);

  return (
    <div className="space-y-8" data-testid="pbx-extensions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Cloud PBX</h1>
          <p className="mt-1 text-sm text-muted-foreground">Extensions, SI Receptionist phrasebook, usage & training — per organization.</p>
        </div>
        <div className="flex items-center gap-2">
          {!orgAdmin && orgs?.orgs?.length > 1 && (
            <select data-testid="pbx-org-select" value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}
              className="rounded-lg border border-white/10 bg-card px-3 py-2 text-sm">
              {orgs.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          {tab === "extensions" && (
            <button data-testid="pbx-add-ext"
              onClick={() => { if (atExtLimit) { toast.error(`Plan limit reached (${plan.extension_limit} extensions). Add a seat below or upgrade.`); return; } setEditing(null); setShowForm(true); }}
              disabled={atExtLimit}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-transform ${atExtLimit ? "cursor-not-allowed bg-secondary text-muted-foreground" : "bg-primary text-primary-foreground hover:scale-105"}`}>
              <Plus size={16} weight="bold" /> New Extension
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-white/10" data-testid="pbx-tabs">
        {[["extensions", "Extensions"], ["phrasebook", "SI Phrasebook"], ["usage", "SI Usage"], ["unrecognized", "Training"], ["settings", "SI Settings"]].map(([k, label]) => (
          <button key={k} data-testid={`pbx-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "extensions" && voiceHealth && <VoiceHealth data={voiceHealth} />}
      {tab === "extensions" && plan && !plan.unlimited && (
        <button data-testid="provision-customer" onClick={() => provision.mutate()} disabled={provision.isPending}
          className="flex w-full items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-primary/5 p-4 text-left transition-colors hover:bg-primary/10 disabled:opacity-50">
          <div>
            <p className="text-sm font-semibold text-primary">⚡ Provision phone system (one-click)</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Applies the standard SI menu — 1 → Booking, 2 → Receptionist, 3 → Sales, 0 → Operator — and grants Business PBX + SI Booking so the SI answers immediately.</p>
          </div>
          <span className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground">{provision.isPending ? "Applying…" : "Apply template"}</span>
        </button>
      )}
      {tab === "extensions" && plan && <PlanPanel plan={plan} orgId={effectiveOrg} />}

      {tab === "phrasebook" && <PbxPhrasebook orgId={effectiveOrg} />}
      {tab === "usage" && <PbxAiUsage orgId={effectiveOrg} />}
      {tab === "unrecognized" && <PbxUnrecognized orgId={effectiveOrg} />}
      {tab === "settings" && <PbxAiSettings orgId={effectiveOrg} />}

      {tab === "extensions" && (isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : extensions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-card p-10 text-center">
          <Phone size={28} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No extensions yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Create your first 5-digit extension to start building the company directory.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {departments.filter((d) => grouped[d.key]?.length).map((d) => (
            <section key={d.key} className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{d.label}</h2>
              <div className="overflow-hidden rounded-xl border border-white/10 bg-card">
                {grouped[d.key].map((e) => (
                  <div key={e.id} data-testid={`ext-row-${e.ext}`} className="grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-3 text-sm last:border-0">
                    <div className="col-span-2 font-mono-metric text-base font-semibold">{e.ext}</div>
                    <div className="col-span-3 truncate">{e.name}</div>
                    <div className="col-span-3 truncate text-xs text-muted-foreground">
                      {e.user_email ? (
                        <span className="inline-flex items-center gap-1">
                          <span className={`h-1.5 w-1.5 rounded-full ${e.online ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                          {e.user_email}
                        </span>
                      ) : <span className="italic">Unassigned</span>}
                    </div>
                    <div className="col-span-2">
                      <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">{PERM_LABEL[e.permission] || e.permission}</span>
                      {!e.e911_configured && (
                        <span title="E911 emergency address not configured" className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-500"><WarningCircle size={12} weight="fill" /> E911</span>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <button data-testid={`ext-edit-${e.ext}`} onClick={() => { setEditing(e); setShowForm(true); }}
                        className="text-muted-foreground hover:text-primary"><PencilSimple size={15} /></button>
                      <button data-testid={`ext-toggle-${e.ext}`} onClick={() => toggleActive.mutate({ id: e.id, active: !e.active })}
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${e.active ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
                        {e.active ? "Active" : "Off"}
                      </button>
                      <button data-testid={`ext-del-${e.ext}`} onClick={() => { if (window.confirm(`Delete extension ${e.ext}?`)) del.mutate(e.id); }}
                        className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {ivrOptions.length > 0 && (
            <section className="space-y-2" data-testid="ivr-menu-options-section">
              <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">IVR Menu Options</h2>
              <div className="overflow-hidden rounded-xl border border-white/10 bg-card">
                {ivrOptions.map((o, i) => (
                  <div key={i} data-testid={`ivr-opt-row-${o.digit}`}
                    className="grid grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-3 text-sm last:border-0">
                    <div className="col-span-2 font-mono-metric text-base font-semibold">Press {o.digit}</div>
                    <div className="col-span-5 truncate">{o.label || <span className="italic text-muted-foreground">Unlabeled</span>}</div>
                    <div className="col-span-5 flex items-center gap-2 truncate text-xs text-muted-foreground">
                      <span>→ <span className="font-mono-metric">{o.destination}</span></span>
                      {DEST_HINT(o.destination) && (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">{DEST_HINT(o.destination)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Read-only. Edit these under <b>Telephony → Business Auto-Attendant (IVR)</b>.</p>
            </section>
          )}
        </div>
      ))}

      {showForm && (
        <ExtForm orgId={effectiveOrg} departments={departments} members={members} editing={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); qc.invalidateQueries({ queryKey: ["pbx-extensions"] }); }} />
      )}
    </div>
  );
}

const FWD_TRIGGERS = [["always", "Always"], ["no_answer", "No answer"], ["busy", "Busy"], ["offline", "Offline"]];
const emptyFwd = () => FWD_TRIGGERS.reduce((a, [k]) => ({ ...a, [k]: { enabled: false, type: "voicemail", target: "" } }), {});

function ExtForm({ orgId, departments, members, editing, onClose, onSaved }) {
  const isEdit = !!editing;
  const [f, setF] = useState({
    department: editing?.department || departments[0]?.key || "support",
    ext: editing?.ext || "", name: editing?.name || "",
    user_id: editing?.user_id || "", permission: editing?.permission || "domestic",
    fallback_type: editing?.fallback_type || "voicemail",
    fallback_number: editing?.fallback_number || "", e911_address: editing?.e911_address || "",
    vm_greeting: editing?.vm_greeting || "",
    forwarding: editing?.forwarding || emptyFwd(),
    active: editing?.active ?? true,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setFwd = (trig, patch) => setF((s) => ({ ...s, forwarding: { ...s.forwarding, [trig]: { ...s.forwarding[trig], ...patch } } }));

  const suggest = useMutation({
    mutationFn: async () => (await api.get("/pbx/extensions/next", { params: orgId ? { org_id: orgId } : {} })).data,
    onSuccess: (d) => set("ext", d.ext),
    onError: (e) => toast.error(apiError(e)),
  });
  React.useEffect(() => { if (!isEdit) suggest.mutate(); /* eslint-disable-next-line */ }, []);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...f, user_id: f.user_id || null };
      if (isEdit) return (await api.put(`/pbx/extensions/${editing.id}`, payload)).data;
      return (await api.post("/pbx/extensions", { ...payload, org_id: orgId || undefined })).data;
    },
    onSuccess: () => { toast.success(`Extension ${f.ext} ${isEdit ? "updated" : "created"}`); onSaved(); },
    onError: (e) => { setErr(apiError(e)); toast.error(apiError(e)); },
  });
  const [err, setErr] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="ext-form-modal" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl border border-white/10 bg-card p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">{isEdit ? `Edit ${editing.ext}` : "New Extension"}</h3>
          <button onClick={onClose} data-testid="ext-form-close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-muted-foreground">Department
            <select data-testid="ext-dept" value={f.department} onChange={(e) => set("department", e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground">
              {departments.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-semibold text-muted-foreground">Extension (5 digits)
              <input data-testid="ext-number" value={f.ext} onChange={(e) => set("ext", e.target.value.replace(/\D/g, "").slice(0, 5))}
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 font-mono-metric text-sm text-foreground disabled:opacity-60" placeholder="10101" />
            </label>
            <label className="block text-xs font-semibold text-muted-foreground">Display name
              <input data-testid="ext-name" value={f.name} onChange={(e) => set("name", e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground" placeholder="Support Agent 1" />
            </label>
          </div>
          <label className="block text-xs font-semibold text-muted-foreground">Assign to member (rings their iYeneh app — free)
            <select data-testid="ext-member" value={f.user_id} onChange={(e) => set("user_id", e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground">
              <option value="">Unassigned (fallback only)</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email} — {m.email}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Calling permission
            <select data-testid="ext-perm" value={f.permission} onChange={(e) => set("permission", e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground">
              <option value="internal">Internal only</option>
              <option value="domestic">Domestic (US/CA)</option>
              <option value="international">International</option>
              <option value="admin">Admin (all)</option>
            </select>
          </label>

          {/* Voicemail greeting */}
          <label className="block text-xs font-semibold text-muted-foreground">Voicemail greeting (spoken to callers)
            <textarea data-testid="ext-vm-greeting" value={f.vm_greeting} onChange={(e) => set("vm_greeting", e.target.value)} rows={2}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground" placeholder="Hi, you've reached Alex. Please leave a message and I'll call you back." />
          </label>

          {/* Advanced call forwarding */}
          <div className="rounded-xl border border-white/10 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><ArrowBendUpRight size={13} /> Advanced call forwarding</p>
            <div className="space-y-2" data-testid="ext-forwarding">
              {FWD_TRIGGERS.map(([trig, label]) => {
                const r = f.forwarding[trig] || { enabled: false, type: "voicemail", target: "" };
                return (
                  <div key={trig} className="rounded-lg bg-background/60 p-2">
                    <label className="flex items-center gap-2 text-xs font-semibold">
                      <input type="checkbox" data-testid={`fwd-${trig}-enabled`} checked={r.enabled} onChange={(e) => setFwd(trig, { enabled: e.target.checked })} className="h-3.5 w-3.5" />
                      {label}
                    </label>
                    {r.enabled && (
                      <div className="mt-1.5 flex gap-2">
                        <select data-testid={`fwd-${trig}-type`} value={r.type} onChange={(e) => setFwd(trig, { type: e.target.value, target: e.target.value === "ai_service" ? "ai_booking" : "" })}
                          className="rounded-lg border border-white/10 bg-background px-2 py-1.5 text-xs">
                          <option value="voicemail">Voicemail</option>
                          <option value="ai_service">SI Service</option>
                          <option value="ext">Extension</option>
                          <option value="ring_group">Ring group</option>
                          <option value="external">External number</option>
                        </select>
                        {r.type === "ai_service" && (
                          <select data-testid={`fwd-${trig}-ai`} value={r.target || "ai_booking"} onChange={(e) => setFwd(trig, { target: e.target.value })}
                            className="flex-1 rounded-lg border border-white/10 bg-background px-2 py-1.5 text-xs">
                            <option value="ai_booking">SI Appointment Booking</option>
                            <option value="ai_receptionist">SI Receptionist</option>
                            <option value="sales">SI Sales</option>
                          </select>
                        )}
                        {r.type !== "voicemail" && r.type !== "ai_service" && (
                          <input data-testid={`fwd-${trig}-target`} value={r.target} onChange={(e) => setFwd(trig, { target: e.target.value })}
                            className="flex-1 rounded-lg border border-white/10 bg-background px-2 py-1.5 font-mono-metric text-xs"
                            placeholder={r.type === "external" ? "+12025551234" : "00100"} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">External forwarding follows PSTN rate &amp; wallet rules.</p>
          </div>

          <label className="block text-xs font-semibold text-muted-foreground">No-answer fallback (legacy)
            <select data-testid="ext-fallback-type" value={f.fallback_type} onChange={(e) => set("fallback_type", e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground">
              <option value="voicemail">Voicemail</option>
              <option value="forward">Forward to number</option>
              <option value="none">None</option>
            </select>
          </label>
          {f.fallback_type === "forward" && (
            <label className="block text-xs font-semibold text-muted-foreground"><DeviceMobile size={12} className="mr-1 inline" />Forward number (E.164)
              <input data-testid="ext-fallback-number" value={f.fallback_number} onChange={(e) => set("fallback_number", e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground" placeholder="+12025551234" />
            </label>
          )}
          <label className="block text-xs font-semibold text-muted-foreground"><ShieldCheck size={12} className="mr-1 inline" />E911 emergency address (US) — optional in Phase 1
            <input data-testid="ext-e911" value={f.e911_address} onChange={(e) => set("e911_address", e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm text-foreground" placeholder="123 Main St, Rockville, MD 20850" />
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          {err && <span data-testid="ext-form-error" className="mr-auto text-xs font-medium text-destructive">{err}</span>}
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">Cancel</button>
          <button data-testid="ext-save" onClick={() => save.mutate()} disabled={save.isPending || !f.ext || !f.name}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
            {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create Extension"}
          </button>
        </div>
      </div>
    </div>
  );
}


function VoiceHealth({ data }) {
  const TONE = {
    ready: { wrap: "border-emerald-500/30 bg-emerald-500/5", dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", label: "SI Voice: Ready" },
    degraded: { wrap: "border-amber-500/30 bg-amber-500/5", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400", label: "SI Voice: Degraded" },
    down: { wrap: "border-rose-500/30 bg-rose-500/5", dot: "bg-rose-500", text: "text-rose-600 dark:text-rose-400", label: "SI Voice: Not ready" },
  };
  const t = TONE[data.status] || TONE.degraded;
  return (
    <div data-testid="pbx-voice-health" className={`rounded-2xl border p-5 ${t.wrap}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${t.dot}`} />
        <h2 className={`text-sm font-semibold ${t.text}`}>{t.label}</h2>
        <span className="text-xs text-muted-foreground">— live status of the deployed phone line</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {data.checks.map((c) => (
          <div key={c.key} data-testid={`voice-check-${c.key}`} className="rounded-xl border border-white/10 bg-card/60 p-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white ${c.ok ? "bg-emerald-500" : "bg-rose-500"}`}>
                {c.ok ? "\u2713" : "\u2717"}
              </span>
              {c.label}
            </div>
            {!c.ok && <p className="mt-1 text-xs text-muted-foreground">{c.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}


function Meter({ label, used, limit }) {
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const full = !unlimited && used >= limit;
  return (
    <div className="flex-1">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-semibold text-muted-foreground">{label}</span>
        <span className={`font-mono-metric font-semibold ${full ? "text-amber-500" : "text-foreground"}`}>
          {used}{unlimited ? "" : ` / ${limit}`}{unlimited ? " · unlimited" : ""}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
          <div className={`h-full rounded-full transition-all ${full ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

function PlanPanel({ plan, orgId }) {
  const qc = useQueryClient();
  const price = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  const buy = useMutation({
    mutationFn: async (kind) => (await api.post(`/pbx/addons/${kind}`, { qty: 1, org_id: orgId || undefined })).data,
    onSuccess: (d, kind) => { toast.success(`Added 1 extra ${kind === "extension" ? "extension seat" : "phone line"}`); qc.invalidateQueries({ queryKey: ["pbx-plan"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (plan.unlimited) {
    return (
      <div data-testid="pbx-plan-panel" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-5 py-4 text-sm">
        <span className="font-semibold text-emerald-500">Platform organization</span>
        <span className="text-muted-foreground"> · unlimited extensions & lines. {plan.used_extensions} extensions in use.</span>
      </div>
    );
  }

  const extFull = plan.used_extensions >= plan.extension_limit;
  return (
    <div data-testid="pbx-plan-panel" className="rounded-2xl border border-white/10 bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            Business PBX <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">{price(plan.plan_price_cents)}/mo</span>
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">Includes {plan.included_lines} phone line & {plan.included_extensions} member extensions.</p>
        </div>
        {!plan.entitled && (
          <span data-testid="pbx-plan-inactive" className="rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-500">Plan inactive — subscribe to add extensions</span>
        )}
      </div>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row">
        <Meter label="Member extensions" used={plan.used_extensions} limit={plan.extension_limit} />
        <Meter label="Phone lines" used={plan.used_lines} limit={plan.line_limit} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button data-testid="addon-extension" onClick={() => buy.mutate("extension")} disabled={buy.isPending}
          className="rounded-full border border-primary/40 px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50">
          + Add extension seat · {price(plan.addon_extension_cents)}/mo
        </button>
        <button data-testid="addon-line" onClick={() => buy.mutate("line")} disabled={buy.isPending}
          className="rounded-full border border-primary/40 px-4 py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50">
          + Add phone line · {price(plan.addon_line_cents)}/mo
        </button>
        {plan.can_override && <span className="text-[11px] text-muted-foreground">Platform staff bypass plan limits.</span>}
      </div>

      {extFull && !plan.can_override && (
        <p data-testid="pbx-plan-limit-note" className="mt-3 rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          You've used all {plan.extension_limit} extensions on your plan. Add a seat above to create more.
        </p>
      )}
    </div>
  );
}
